import type { FormData as ProductFormData } from '../types'

/**
 * PE-2099 — the sale-mode slice of the product create/update payload, shared by
 * the add and edit transforms.
 *
 * rack-api's CreateProductDto guards the pricing fields with
 * `@ValidateIf(saleMode === PRICE)`, so a CONSULTATION_REQUIRED product must
 * omit them entirely rather than send a zero — a sent `basePrice: 0` skips
 * validation and still persists.
 */
export const buildSaleModePayload = (formData: ProductFormData) => {
  const saleMode = formData.saleMode === 'CONSULTATION_REQUIRED' ? 'CONSULTATION_REQUIRED' : 'PRICE'

  if (saleMode === 'CONSULTATION_REQUIRED') {
    return { saleMode, bookingServiceId: formData.bookingServiceId }
  }

  return {
    saleMode,
    basePrice: parseInt(formData.price) || 0,
    baseCostPrice: parseInt(formData.baseCostPrice) || 0,
    discountPercentage: formData.discountPercentage || 0,
  }
}

/**
 * PE-2099 — client-side pairing rule.
 *
 * The draft endpoint takes `CreateDraftDto = PartialType(OmitType(CreateProductDto, ['status']))`,
 * whose `@IsOptional()` short-circuits the `@ValidateIf` guards. A draft can therefore
 * hold CONSULTATION_REQUIRED with no bookingServiceId and only fail at publish, so the
 * form — not the draft POST — has to enforce the pair.
 */
export const isSaleModeValid = (formData: ProductFormData): boolean =>
  formData.saleMode === 'CONSULTATION_REQUIRED'
    ? !!formData.bookingServiceId
    : !!formData.price
